/**
 * Runner movement — pure. Auto-runs forward (+Z), steers laterally (X), and
 * jumps/crouches (Y). Obstacles block forward progress (non-lethal) until the
 * player jumps over, crouches under, or steers around them. Footsteps fire by
 * forward distance travelled.
 */
import type { SimState, SimInput, SimConfig, SimEvent } from './types.js';
import type { LevelDef, Obstacle } from '../level/types.js';
import { rangesOverlap } from './collision.js';

function solidY(o: Obstacle, floorY: number): [number, number] {
  if (o.kind === 'hurdle') return [floorY, o.topY ?? floorY];
  if (o.kind === 'bar') return [o.botY ?? 0, Infinity];
  return [floorY, Infinity]; // wall
}

export function updatePlayer(
  state: SimState,
  input: SimInput,
  dt: number,
  level: LevelDef,
  cfg: SimConfig,
  events: SimEvent[],
): void {
  const p = state.player;
  const pw = cfg.playerW / 2;
  const pd = cfg.playerD / 2;
  const wasGrounded = p.grounded;

  // --- crouch (keep feet anchored; forced to stay crouched under a bar) ---
  const oldHH = (p.crouching ? cfg.crouchH : cfg.playerH) / 2;
  const feet = p.pos.y - oldHH;
  let crouching = input.crouch;
  if (!crouching) {
    const standHead = feet + cfg.playerH;
    for (const o of level.obstacles) {
      if (o.kind !== 'bar') continue;
      if (
        rangesOverlap(p.pos.x - pw, p.pos.x + pw, o.xMin, o.xMax) &&
        rangesOverlap(p.pos.z - pd, p.pos.z + pd, o.zMin, o.zMax) &&
        standHead > (o.botY ?? Infinity)
      ) {
        crouching = true;
        break;
      }
    }
  }
  p.crouching = crouching;
  const hh = (crouching ? cfg.crouchH : cfg.playerH) / 2;
  p.pos.y = feet + hh;

  // --- lateral steer ---
  p.pos.x += input.steer * cfg.lateralSpeed * dt;
  const lim = cfg.trackHalfWidth - pw;
  if (p.pos.x < -lim) p.pos.x = -lim;
  else if (p.pos.x > lim) p.pos.x = lim;

  // --- vertical (jump + gravity) ---
  if (p.jumpBuffer > 0) p.jumpBuffer -= dt;
  if (input.jump) p.jumpBuffer = cfg.jumpBufferTime;
  p.vy -= cfg.gravity * dt;
  if (p.jumpBuffer > 0 && p.coyoteTimer <= cfg.coyoteTime) {
    p.vy = cfg.jumpVel;
    p.coyoteTimer = cfg.coyoteTime + 1;
    p.jumpBuffer = 0;
    events.push({ type: 'jump' });
  }
  p.pos.y += p.vy * dt;
  if (p.pos.y - hh <= level.floorY) {
    p.pos.y = level.floorY + hh;
    p.vy = 0;
    p.grounded = true;
    p.coyoteTimer = 0;
    if (!wasGrounded) events.push({ type: 'land' });
  } else {
    p.grounded = false;
    p.coyoteTimer += dt;
  }

  // --- forward auto-run, then block against obstacles / the gate ---
  const prevZ = p.pos.z;
  p.pos.z += cfg.forwardSpeed * dt;
  const head = p.pos.y + hh;
  const feetNow = p.pos.y - hh;

  let blockZ = Infinity;
  let blocker = -1;
  for (let i = 0; i < level.obstacles.length; i++) {
    const o = level.obstacles[i]!;
    if (!rangesOverlap(p.pos.x - pw, p.pos.x + pw, o.xMin, o.xMax)) continue;
    const [sMin, sMax] = solidY(o, level.floorY);
    if (!rangesOverlap(feetNow, head, sMin, sMax)) continue;
    if (p.pos.z + pd > o.zMin && p.pos.z - pd < o.zMax) {
      const cand = o.zMin - pd;
      if (cand < blockZ) {
        blockZ = cand;
        blocker = i;
      }
    }
  }
  if (!state.complete && p.pos.z + pd > level.gateZ) {
    const cand = level.gateZ - pd;
    if (cand < blockZ) {
      blockZ = cand;
      blocker = -1; // the gate is expected; not a "bump"
    }
  }
  if (blockZ < p.pos.z) p.pos.z = blockZ;

  if (blocker !== state.nearObstacle) {
    if (blocker !== -1) events.push({ type: 'bump', obstacle: blocker });
    state.nearObstacle = blocker;
  }

  // --- footsteps by forward distance actually travelled ---
  const fwd = p.pos.z - prevZ;
  if (p.grounded && fwd > 0) {
    p.strideAccum += fwd;
    while (p.strideAccum >= cfg.strideLength) {
      p.strideAccum -= cfg.strideLength;
      events.push({ type: 'footstep' });
    }
  }
}
